import { TOCNode } from '../../../types/DocumentTypes';
import {
  HighlightPositionPoint,
  HighlightPosition,
} from '../../Annotation/types';
import { isValidBlockIdString, parseBlockId } from '../../../utils/BlockUtils';

const WHITESPACE_REGEX = /\s/;
const WHITESPACE_REGEX_GLOBAL = /\s/g;

export function getNonWhitespaceCount(str: string): number {
  return str.replace(WHITESPACE_REGEX_GLOBAL, '').length;
}

function getBlockIdString(node: Node): string | null {
  /* If we reach the document node, we've traversed the entire DOM tree and
  haven't found a valid block ID. */
  if (node.nodeType === Node.DOCUMENT_NODE) {
    return null;
  }
  // If the node is a text node, get the block ID from the parent element
  // If the node is an element node, get the block ID directly from the element
  const initialElement =
    node.nodeType === Node.TEXT_NODE
      ? node.parentElement
      : (node as HTMLElement);

  const id = initialElement?.id ?? null;

  if (isValidBlockIdString(id)) {
    return id;
  }

  if (node.parentNode) {
    return getBlockIdString(node.parentNode);
  }

  return null;
}

function getBlockId(node: Node | null): number | null {
  if (!node) {
    return null;
  }

  const blockId = getBlockIdString(node);

  if (!isValidBlockIdString(blockId)) {
    return null;
  }

  try {
    return parseBlockId(blockId as string); // We know that blockId is a valid blockId string.
  } catch (_parsingError) {
    // TODO: Log error
    return null;
  }
}

/**
 * A selection direction that can be used to determine the highlight start and
 * end positions from the Selection object.
 */
type DeterminedSelectionDirection = 'forward' | 'backward';

enum SelectionDirection {
  Forward = 'forward',
  Backward = 'backward',
  None = 'none',
}

/**
 * Determined the selection direction from the Selection object, even in cases
 * where the selection direction is not explicitly set.
 */
function determineSelectionDirection(
  selection: Selection,
): DeterminedSelectionDirection | null {
  if (!selection.anchorNode || !selection.focusNode) {
    return null;
  }

  if (
    selection.direction === SelectionDirection.Forward ||
    selection.direction === SelectionDirection.Backward
  ) {
    return selection.direction;
  }

  /* If the anchor and focus nodes are the same, we can determine the document
  order by simply comparing the offsets. */
  if (selection.anchorNode === selection.focusNode) {
    if (selection.anchorOffset <= selection.focusOffset) {
      return 'forward';
    } else {
      return 'backward';
    }
  }

  const position = selection.anchorNode.compareDocumentPosition(
    selection.focusNode,
  );

  if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
    // anchorNode comes before focusNode
    return 'forward';
  } else {
    // focusNode comes before anchorNode
    return 'backward';
  }
}

function getClosestBlockElement(node: Node): Element | null {
  let el: Element | null =
    node.nodeType === Node.TEXT_NODE
      ? (node.parentElement as Element | null)
      : (node as Element);
  while (el && !isValidBlockIdString(el.id)) {
    el = el.parentElement;
  }
  return el;
}

/**
 * Removes leading and trailing whitespace from the selection offsets.
 */
function normalizeSelectionOffsets(
  node: Node,
  startOffset: number,
  endOffset: number,
): { startOffset: number; endOffset: number } {
  const text = node.textContent ?? '';

  // Find the first non-whitespace character from startOffset
  let adjustedStart = startOffset;
  while (
    adjustedStart < endOffset &&
    WHITESPACE_REGEX.test(text[adjustedStart])
  ) {
    adjustedStart++;
  }

  // Find the last non-whitespace character before endOffset
  let adjustedEnd = endOffset;
  while (
    adjustedEnd > adjustedStart &&
    WHITESPACE_REGEX.test(text[adjustedEnd - 1])
  ) {
    adjustedEnd--;
  }

  return { startOffset: adjustedStart, endOffset: adjustedEnd };
}

/**
 * Maps a node offset to a block offset (block offset ignores whitespace).
 */
function mapToBlockOffset(node: Node, offsetInNode: number): number | null {
  const blockEl = getClosestBlockElement(node);
  if (!blockEl) {
    return null;
  }

  const doc = blockEl.ownerDocument ?? document;
  const walker = doc.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
  let total = 0;
  let current: Node | null = walker.nextNode();

  while (current) {
    const text = current.textContent ?? '';

    if (current !== node) {
      total += getNonWhitespaceCount(text);
      current = walker.nextNode();
    } else {
      const adjustedOffset = findNextNonWhitespaceOffset(text, offsetInNode);
      total += getNonWhitespaceCount(text.slice(0, adjustedOffset));
      return total;
    }
  }

  return total;
}

function findNextNonWhitespaceOffset(
  text: string,
  startOffset: number,
): number {
  let idx = startOffset;
  while (idx < text.length && WHITESPACE_REGEX.test(text[idx])) {
    idx++;
  }
  return idx;
}

/**
 * Returns the highlight position from a selection, and null if the highlight
 * position cannot be determined.
 */
export function getHighlightPosition(
  selection: Selection,
): HighlightPosition | null {
  if (
    selection.anchorNode?.nodeType !== Node.TEXT_NODE ||
    selection.focusNode?.nodeType !== Node.TEXT_NODE
  ) {
    // TODO: Log error
    return null;
  }

  const anchorBlockId = getBlockId(selection.anchorNode); // Where user started selection
  const focusBlockId = getBlockId(selection.focusNode); // Where user ended selection

  if (anchorBlockId === null || focusBlockId === null) {
    // Do not continue if the anchor or focus block ID cannot be determined.
    return null;
  }

  // Adjust offsets to exclude leading/trailing whitespace
  // For single-node selections, trim whitespace from both ends
  let adjustedAnchorOffset = selection.anchorOffset;
  let adjustedFocusOffset = selection.focusOffset;

  if (selection.anchorNode === selection.focusNode) {
    // Same node selection - trim whitespace from both ends
    const adjusted = normalizeSelectionOffsets(
      selection.anchorNode,
      selection.anchorOffset,
      selection.focusOffset,
    );
    adjustedAnchorOffset = adjusted.startOffset;
    adjustedFocusOffset = adjusted.endOffset;
  }

  const finalAnchorOffset = mapToBlockOffset(
    selection.anchorNode,
    adjustedAnchorOffset,
  );
  const finalFocusOffset = mapToBlockOffset(
    selection.focusNode,
    adjustedFocusOffset,
  );

  if (finalAnchorOffset === null || finalFocusOffset === null) {
    return null;
  }

  const anchorPosition: HighlightPositionPoint = {
    blockId: anchorBlockId,
    offset: finalAnchorOffset,
  };

  const focusPosition: HighlightPositionPoint = {
    blockId: focusBlockId,
    offset: finalFocusOffset,
  };

  const determinedSelectionDirection = determineSelectionDirection(selection);
  if (!determinedSelectionDirection) {
    // The selection or its order cannot be determined.
    return null;
  }

  return determinedSelectionDirection === SelectionDirection.Forward
    ? { start: anchorPosition, end: focusPosition }
    : { start: focusPosition, end: anchorPosition };
}

/**
 * Calculates the TOC node path for a highlight. Returns an array of TOC nodes,
 * starting from the root node and ending at the deepest nested node in the path.
 */
export function getHighlightPath(
  positionRange: HighlightPosition,
  tocTree: TOCNode[],
): TOCNode[] {
  const path: TOCNode[] = [];

  findNodeByPosition(path, positionRange, tocTree);

  return path;
}

function findNodeByPosition(
  path: TOCNode[],
  position: HighlightPosition,
  tocTree: TOCNode[],
): void {
  const { start, end } = position;
  const startBlockIndex = start.blockId;
  const endBlockIndex = end.blockId;

  for (const node of tocTree) {
    const nodeStartBlockIndex = node.blockId;
    const nodeEndBlockIndex = node.blockId + node.blockCount;

    if (
      startBlockIndex >= nodeStartBlockIndex &&
      endBlockIndex <= nodeEndBlockIndex
    ) {
      path.push(node);

      if (node.children) {
        findNodeByPosition(path, position, node.children);
      }

      break;
    }
  }
}
